import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu as MenuIcon, X } from "lucide-react";
import { NAV_LINKS, BUSINESS } from "../data/content";
import { Wordmark } from "./Logo";
import { scrollToId } from "../utils/scroll";

export const Navbar = ({ onReserve }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed inset-x-0 top-0 z-[80] transition-[background-color,border-color] duration-500 ${
          scrolled || open
            ? "border-b border-[rgba(247,244,239,0.1)] bg-[#141210]/85 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
      >
        <nav
          className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8"
          aria-label="Primary"
        >
          <button
            type="button"
            onClick={() => go("home")}
            data-testid="nav-logo-button"
            aria-label="Roma's Café Diner — back to top"
          >
            <Wordmark compact />
          </button>
          <ul className="hidden items-center gap-7 lg:flex">
            {NAV_LINKS.map((l) => (
              <li key={l.id}>
                <button
                  type="button"
                  data-testid={`nav-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-sm font-medium text-[#b5afa7] transition-colors duration-200 hover:text-[#d4af37]"
                >
                  {l.label}
                </button>
              </li>
            ))}
          </ul>
          <div className="hidden lg:block">
            <button
              type="button"
              className="btn-gold !px-6 !py-2.5"
              data-testid="nav-reserve-button"
              onClick={onReserve}
            >
              Reserve a Table
            </button>
          </div>
          <button
            type="button"
            className="rounded-full border border-[rgba(247,244,239,0.2)] p-2.5 text-[#f7f4ef] lg:hidden"
            data-testid="mobile-menu-button"
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={20} /> : <MenuIcon size={20} />}
          </button>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[70] flex flex-col justify-center bg-[#141210]/97 px-8 backdrop-blur-xl lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ul className="space-y-2">
              {NAV_LINKS.map((l, i) => (
                <motion.li
                  key={l.id}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <button
                    type="button"
                    data-testid={`mobile-nav-link-${l.id}`}
                    onClick={() => go(l.id)}
                    className="font-display block w-full py-3 text-left text-3xl font-semibold text-[#f7f4ef] transition-colors hover:text-[#d4af37]"
                  >
                    {l.label}
                  </button>
                </motion.li>
              ))}
            </ul>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.4 }}
              className="mt-10 space-y-4"
            >
              <button
                type="button"
                className="btn-gold w-full"
                data-testid="mobile-reserve-button"
                onClick={() => {
                  setOpen(false);
                  onReserve();
                }}
              >
                Reserve a Table
              </button>
              <a
                href={BUSINESS.phones[0].href}
                data-testid="mobile-call-link"
                className="block text-center text-sm text-[#b5afa7]"
              >
                or call {BUSINESS.phones[0].label}
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
